import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Globe, Plus, Trash2, Search, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAiQuota } from '@/lib/useAiQuota';
import { AiQuotaInline } from '@/components/AiQuotaInline';

export function CompetitorsTab() {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { quota, refresh: refreshQuota, isReached } = useAiQuota();

  const t = (fr, en) => lang === 'fr' ? fr : en;

  const handleAiError = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (d && typeof d === 'object' && d.error === 'ai_quota_reached') {
      toast.error(t(
        'Limite IA atteinte pour ce mois. Passez au forfait supérieur ou contactez l\'administrateur.',
        'AI limit reached for this month. Upgrade your plan or contact your administrator.'
      ));
      refreshQuota();
      return;
    }
    toast.error((typeof d === 'string' ? d : null) || fallback);
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/seo/competitors');
      setCompetitors(data.competitors);
    } catch { toast.error(t('Erreur de chargement','Loading error')); }
    finally { setLoading(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post('/seo/competitors/analyze');
      setCompetitors(data.competitors);
      toast.success(t(`${data.count} concurrents detectes`,`${data.count} competitors detected`));
      refreshQuota();
    } catch (e) {
      handleAiError(e, t('Erreur d\'analyse','Analysis error'));
    } finally { setAnalyzing(false); }
  };

  const addManual = async () => {
    if (!addUrl) return;
    setAdding(true);
    try {
      const { data } = await api.post('/seo/competitors/add', { url: addUrl, name: addName });
      setCompetitors(prev => [data, ...prev]);
      setShowAdd(false); setAddUrl(''); setAddName('');
      toast.success(t('Concurrent ajoute','Competitor added'));
      refreshQuota();
    } catch (e) {
      handleAiError(e, t('Erreur','Error'));
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('Supprimer ce concurrent?','Delete this competitor?'))) return;
    try {
      await api.delete(`/seo/competitors/${id}`);
      setCompetitors(prev => prev.filter(c => c.competitor_id !== id));
    } catch { toast.error(t('Erreur','Error')); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-competitors-tab">
      {isAdmin && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-3">
            <Button onClick={analyze} disabled={analyzing || isReached} data-testid="analyze-competitors-btn">
              {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              {isReached
                ? t('Quota atteint','Quota reached')
                : (analyzing ? t('Analyse en cours...','Analyzing...') : t('Analyser les concurrents','Analyze competitors'))}
            </Button>
            <Button variant="outline" onClick={() => setShowAdd(true)} disabled={isReached} data-testid="add-competitor-btn">
              <Plus className="mr-2 h-4 w-4" />{t('Ajouter un concurrent','Add competitor')}
            </Button>
          </div>
          <AiQuotaInline quota={quota} />
        </div>
      )}

      {competitors.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Globe className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p className="font-medium">{t('Aucun concurrent','No competitors')}</p>
          <p className="text-sm">{t('Lancez une analyse pour detecter vos concurrents','Run an analysis to detect your competitors')}</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {competitors.map(c => (
            <Card key={c.competitor_id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setExpanded(expanded === c.competitor_id ? null : c.competitor_id)}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-lg">{c.name}</h3>
                      <Badge variant={c.source === 'auto' ? 'secondary' : 'outline'}>{c.source}</Badge>
                      {c.position > 0 && <Badge className="bg-blue-500/10 text-blue-600">#{c.position}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{c.url}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {c.keywords?.slice(0,5).map((kw,i) => <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${c.seo_score >= 70 ? 'text-green-600' : c.seo_score >= 40 ? 'text-amber-500' : 'text-red-500'}`}>{c.seo_score}</div>
                      <div className="text-xs text-muted-foreground">SEO</div>
                    </div>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={(e) => { e.stopPropagation(); remove(c.competitor_id); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {expanded === c.competitor_id && (
                  <div className="mt-4 pt-4 border-t grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="font-medium mb-1">{t('Services','Services')}</p>
                      <ul className="text-muted-foreground space-y-0.5">{c.services?.map((s,i) => <li key={i}>- {s}</li>)}</ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">{t('Pages locales','Local pages')}</p>
                      <ul className="text-muted-foreground space-y-0.5">{c.local_pages?.map((p,i) => <li key={i} className="truncate">{p}</li>)}</ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">{t('Avis Google','Google reviews')}</p>
                      <ul className="text-muted-foreground space-y-0.5">{c.review_themes?.map((r,i) => <li key={i}>- {r}</li>)}</ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">{t('Trafic mensuel','Monthly traffic')}</p>
                      <p className="text-muted-foreground">{c.monthly_traffic}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('Ajouter un concurrent','Add competitor')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="https://example.com" value={addUrl} onChange={e => setAddUrl(e.target.value)} data-testid="competitor-url-input" />
            <Input placeholder={t('Nom (optionnel)','Name (optional)')} value={addName} onChange={e => setAddName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>{t('Annuler','Cancel')}</Button>
            <Button onClick={addManual} disabled={adding || !addUrl}>
              {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('Ajouter','Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
